import React from "react";
import { AlertTriangle } from "lucide-react";

export const fmtNum = (v) => (v == null ? "—" : Number(v).toLocaleString("pt-BR"));
export const fmtPct = (v) => (v == null ? "—" : `${Number(v).toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`);
export const fmtDate = (iso, withTime = true) => {
  if (!iso) return "—";
  const d = new Date(/[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
  return d.toLocaleString("pt-BR", withTime ? { dateStyle: "short", timeStyle: "short" } : { dateStyle: "short" });
};

export function AnimatedCounter({ value = 0, decimals = 0 }) {
  return <span className="mono">{Number(value || 0).toLocaleString("pt-BR", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}</span>;
}

export function PageHeader({ title, meta, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
      <div>
        <h1 className="text-[20px] font-semibold tracking-[-0.01em]">{title}</h1>
        {meta && <p className="text-[13px] text-[var(--muted)] mt-0.5">{meta}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

export function Panel({ children, className = "", title, action, description, testid }) {
  return (
    <section className={`mia-panel ${className}`} data-testid={testid}>
      {(title || action) && (
        <header className="flex items-center justify-between gap-3 px-4 pt-3.5 pb-2.5">
          <div>
            {title && <h3 className="ops-label">{title}</h3>}
            {description && <p className="ops-help mt-0.5 normal-case">{description}</p>}
          </div>
          {action}
        </header>
      )}
      <div>{children}</div>
    </section>
  );
}

export function Delta({ now, prev, invert = false, unit = "" }) {
  if (prev == null || now == null) return null;
  const diff = now - prev;
  if (prev === 0) {
    return <span className="text-[11px] text-[var(--muted2)] mono">ant. {fmtNum(prev)}{unit}</span>;
  }
  const pct = (diff / prev) * 100;
  const good = invert ? diff < 0 : diff > 0;
  const color = diff === 0 ? "var(--muted)" : good ? "var(--ok)" : "var(--bad)";
  return (
    <span className="text-[11px] mono" style={{ color }} title={`Período anterior: ${fmtNum(prev)}${unit}`}>
      {diff > 0 ? "+" : ""}{pct.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%
    </span>
  );
}

// Metric cell, meant to live inside <MetricStrip>
export function KpiCard({ label, value, hint, decimals = 0, suffix = "", prev, testid }) {
  const id = testid || `kpi-${label.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <div className="px-4 py-3.5 min-w-0" data-testid={id}>
      <p className="ops-label truncate">{label}</p>
      <div className="mt-1.5 flex items-baseline gap-2">
        <span className="text-[22px] font-medium mono leading-none">
          {value == null ? "—" : `${Number(value).toLocaleString("pt-BR", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}${suffix}`}
        </span>
        <Delta now={value} prev={prev} unit={suffix} />
      </div>
      {hint && <p className="ops-help ops-kpi-hint mt-1 truncate" title={hint}>{hint}</p>}
    </div>
  );
}

export function MetricStrip({ children, cols = 4, className = "" }) {
  const map = { 3: "grid-cols-1 sm:grid-cols-3", 4: "grid-cols-2 lg:grid-cols-4", 5: "grid-cols-2 md:grid-cols-3 lg:grid-cols-5", 6: "grid-cols-2 md:grid-cols-3 xl:grid-cols-6" };
  return <div className={`ops-strip ${map[cols] || map[4]} ${className}`}>{children}</div>;
}

export function Skeleton({ className = "", style }) {
  return <div className={`mia-skeleton ${className}`} style={style} aria-hidden="true" />;
}

export function Spinner({ size = 16 }) {
  return <span className="ops-spinner" style={{ width: size, height: size }} role="status" aria-label="Carregando" />;
}

const TONES = {
  accent: ["var(--accent-weak)", "var(--accent-line)", "var(--accent-ink)"],
  ok: ["rgba(98,180,122,0.1)", "rgba(98,180,122,0.35)", "var(--ok)"],
  warn: ["rgba(214,160,60,0.1)", "rgba(214,160,60,0.35)", "var(--warn)"],
  bad: ["rgba(226,102,94,0.1)", "rgba(226,102,94,0.38)", "var(--bad)"],
  muted: ["transparent", "var(--border-strong)", "var(--muted)"],
};
TONES.purple = TONES.accent;
TONES.lime = TONES.ok;

export function Badge({ children, tone = "muted", testid }) {
  const [bg, bd, fg] = TONES[tone] || TONES.muted;
  return (
    <span data-testid={testid} className="inline-flex items-center gap-1 h-[20px] px-1.5 rounded-[3px] text-[11px] font-medium whitespace-nowrap"
      style={{ background: bg, border: `1px solid ${bd}`, color: fg }}>
      {children}
    </span>
  );
}

export function EmptyState({ title = "Sem dados no período", description, icon: Icon, action }) {
  return (
    <div className="px-4 py-8" data-testid="empty-state">
      <div className="flex items-start gap-2.5">
        {Icon && <Icon size={16} className="mt-0.5 shrink-0 text-[var(--muted2)]" aria-hidden="true" />}
        <div>
          <p className="text-[13px] font-medium">{title}</p>
          {description && <p className="ops-help mt-0.5 max-w-md">{description}</p>}
          {action && <div className="mt-3">{action}</div>}
        </div>
      </div>
    </div>
  );
}

export function ErrorState({ message = "Não foi possível carregar os dados.", onRetry }) {
  return (
    <div role="alert" data-testid="error-state" className="flex items-center gap-3 px-4 py-3 mb-4 rounded-[var(--radius)] text-[13px]"
      style={{ background: "rgba(226,102,94,0.07)", border: "1px solid rgba(226,102,94,0.35)" }}>
      <AlertTriangle size={15} style={{ color: "var(--bad)" }} aria-hidden="true" />
      <span className="flex-1">{message}</span>
      {onRetry && <Button variant="outline" size="sm" onClick={onRetry} data-testid="error-retry">Tentar novamente</Button>}
    </div>
  );
}

export function Button({ children, variant = "primary", size, className = "", ...props }) {
  return (
    <button className={`ops-btn ops-btn-${variant} ${size === "sm" ? "ops-btn-sm" : ""} ${className}`} {...props}>
      {children}
    </button>
  );
}
